// localStorage as rule data (`localStorage.<key>` in JSONLogic rules), kept
// reactive. A same-document write fires no `storage` event, so writes are caught
// by wrapping Storage's mutating methods, and a light poll catches edits that
// bypass them (e.g. the DevTools Application panel).

type Listener = () => void;

const POLL_INTERVAL_MS = 1000;

const listeners = new Set<Listener>();

// Storage can be blocked (e.g. third-party storage restrictions), so every access is guarded.
const readLocalStorage = (): Record<string, string> => {
  try {
    const entries: Record<string, string> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key !== null) entries[key] = localStorage.getItem(key) ?? "";
    }
    return entries;
  } catch {
    return {};
  }
};

let snapshot: Record<string, string> = readLocalStorage();
let serializedSnapshot = JSON.stringify(snapshot);

// Only a real change produces a new snapshot object, so rules re-evaluate on
// changes and not on every poll or unrelated write.
const refresh = () => {
  const next = readLocalStorage();
  const serializedNext = JSON.stringify(next);
  if (serializedNext === serializedSnapshot) return;
  snapshot = next;
  serializedSnapshot = serializedNext;
  listeners.forEach((listener) => listener());
};

let isStoragePatched = false;

// Any localStorage write in this document - merchant scripts, the console -
// re-evaluates rules, not just writes made through setRuleLocalStorageItem.
const patchStorageWrites = () => {
  if (isStoragePatched) return;
  isStoragePatched = true;
  try {
    const prototype = Storage.prototype;
    (["setItem", "removeItem", "clear"] as const).forEach((method) => {
      const original = prototype[method] as (...args: unknown[]) => unknown;
      (prototype as any)[method] = function (this: Storage, ...args: unknown[]) {
        const result = original.apply(this, args);
        if (this === window.localStorage) queueMicrotask(refresh);
        return result;
      };
    });
  } catch {
    // Storage unavailable: polling still picks up changes where possible.
  }
};

let pollTimer: ReturnType<typeof setInterval> | null = null;

export const subscribeRuleLocalStorage = (listener: Listener) => {
  listeners.add(listener);
  if (listeners.size === 1) {
    patchStorageWrites();
    // Writes from other tabs/iframes of the same origin.
    window.addEventListener("storage", refresh);
    pollTimer = setInterval(refresh, POLL_INTERVAL_MS);
    // Anything written before the first subscriber.
    refresh();
  }
  return () => {
    listeners.delete(listener);
    if (!listeners.size) {
      window.removeEventListener("storage", refresh);
      if (pollTimer) clearInterval(pollTimer);
      pollTimer = null;
    }
  };
};

export const getRuleLocalStorageSnapshot = () => snapshot;

/** Persists a value (null/undefined removes the key) and re-evaluates rules that read it. */
export const setRuleLocalStorageItem = (key: string, value: unknown) => {
  if (!key) return;
  try {
    if (value === null || value === undefined) localStorage.removeItem(key);
    else localStorage.setItem(key, typeof value === "string" ? value : JSON.stringify(value));
  } catch {
    // Storage unavailable: the rule keeps reading the last snapshot.
  }
  refresh();
};
